import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { DotCMSClient } from '@dotcms/angular';
import { createDotCMSClient } from '@dotcms/client';
import {
  DotCMSClientConfig,
  DotCMSComposedPageAsset,
  DotCMSComposedPageResponse,
  DotCMSExtendedPageResponse,
  DotCMSPageRequestParams,
} from '@dotcms/types';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ConfigService } from 'src/app/extensions/dot-cms-uve/services/dot-cms-uve-config/dot-cms-config.service';

import { getCurrentDotCmsApiConfiguration } from 'ish-core/store/core/configuration';
import { DomService } from 'ish-core/utils/dom/dom.service';

import { buildPageQuery, buildQuery } from '../../utils/built-query-utils';

@Injectable({ providedIn: 'root' })
export class DotCmsUvePageService {
  private configService = inject(ConfigService);
  private httpClient = inject(HttpClient);
  private store = inject(Store);
  private domService = inject(DomService);
  private document = inject<Document>(DOCUMENT);

  private clientSig = signal<DotCMSClient | null>(null);

  constructor() {
    this.configService
      .getConfig$()
      .pipe(
        filter((config): config is DotCMSClientConfig => !!config),
        tap(config => this.setCSS(config)),
        map(config => ({
          ...config,
        }))
      )
      .subscribe({
        next: config => {
          const client = createDotCMSClient(config);
          this.clientSig.set(client);
        },
        error: err => console.error('Config stream error', err),
        complete: () => console.debug('Config stream completed'),
      });
  }
  setCSS(config: DotCMSClientConfig): void {
    if (!SSR) {
      const link = this.domService.createElement<HTMLLinkElement>('link', this.document.head);
      this.domService.setProperty(link, 'rel', 'stylesheet');
      this.domService.setProperty(link, 'type', 'text/css');
      this.domService.setProperty(
        link,
        'href',
        `${config.dotcmsUrl}/application/styles.dotsass?host_id=${config.siteId}`
      );
    }
  }

  /**
   * Get the page and navigation for the given url and params.
   */
  getPageAsset<T extends DotCMSExtendedPageResponse>(
    url: string,
    params: DotCMSPageRequestParams
  ): Observable<DotCMSComposedPageResponse<T>> {
    return this.configService.getConfig$().pipe(
      filter((config): config is DotCMSClientConfig => !!config),
      switchMap(() =>
        from(
          this.clientSig().page.get<T>(url, {
            ...params,
          })
        ).pipe(
          catchError((error: DotCMSComposedPageResponse<T>) => {
            console.error('DotCMS error', error);
            return of(error);
          })
        )
      )
    );
  }
  getBlogDetailPageAsset(
    url: string,
    params: DotCMSPageRequestParams
  ): Observable<DotCMSComposedPageResponse<unknown>> {
    const contentQuery = buildQuery(params.graphql.content);
    const pageQuery = buildPageQuery({
      page: params.graphql.page,
      fragments: params.graphql.fragments,
      additionalQueries: contentQuery,
    });
    return combineLatest([this.store.pipe(select(getCurrentDotCmsApiConfiguration))]).pipe(
      map(([config]) => config),
      filter(config => config !== undefined),
      switchMap(config => {
        const body = {
          query: pageQuery,
          variables: { url, mode: 'LIVE', languageId: params.languageId, fireRules: false, siteId: config.hostId },
        };
        const headers = new HttpHeaders({
          Authorization: `Bearer ${config.token}`,
          'Content-Type': 'application/json',
        });
        return this.httpClient.post(`${config.host}/api/v1/graphql`, body, { headers });
      }),
      map(pageResponse => {
        //@ts-ignore
        const { footer, navigation, page } = pageResponse.data;
        const { urlContentMap } = page;
        const { _map, author, blogComment, blogContent, imageBlog } = urlContentMap;
        const mapTest = {
          ..._map,
          author,
          blogComment,
          blogContent,
          imageBlog,
        };
        const pageTest = {
          ...page,
          urlContentMap: mapTest,
        };
        return {
          pageAsset: pageTest as DotCMSComposedPageAsset<DotCMSExtendedPageResponse>,
          content: {
            navigation,
            footer,
          },
          graphql: { query: pageQuery, variables: undefined },
        };
      })
    );
  }
}
